import type { PairSummaryDto } from '@rhcst/shared';

/**
 * Where a pair came from, in one chip.
 *
 * The distinction this badge carries is the point of the whole scanner
 * redesign: a launchpad said a token was created, or a pool merely appeared.
 * Those look identical in a feed unless something says otherwise, and for a
 * long time nothing did.
 *
 * Three display rules, in the same spirit as the risk badge's invariants:
 *
 *  1. **Unclassified is not "ordinary".** A pair discovered before this scanner
 *     has no classification. It renders as an explicit "unclassified" chip, not
 *     as a blank — an absent label read as a clean one is how a DEX pool gets
 *     mistaken for a launch, which is the bug being fixed.
 *
 *  2. **A launch and a pool never share a tone.** They are different claims
 *     backed by different evidence, so they are never allowed to look alike.
 *
 *  3. **Blocked is stated, not implied.** A pair the source gate refuses shows
 *     why, rather than quietly being absent from a list somewhere.
 */

type Origin = NonNullable<PairSummaryDto['origin']>;

const LABELS: Record<string, string> = {
  PONS_TOKEN_LAUNCHED: 'Pons launch',
  POOLS_TRADE_TOKEN_CREATED: 'pools.trade created',
  POOLS_TRADE_INSTANT_LAUNCHED: 'pools.trade launch',
  POOLS_TRADE_CROWD_AUCTION_CREATED: 'Crowd auction',
  POOLS_TRADE_CROWD_TRADABLE: 'Crowd launch live',
  POOLS_FUN_TOKEN_LAUNCHED: 'pools.fun launch',
  LAUNCHPAD_TOKEN_CREATED: 'Launchpad token',
  DEX_NATIVE_TOKEN_CANDIDATE: 'DEX-native candidate',
  KNOWN_TOKEN_NEW_LP: 'Known token, new LP',
  DEX_POOL_CREATED: 'DEX pool',
  WATCHED_TOKEN_LIQUIDITY_UPDATE: 'Liquidity update',
};

/** A launch is a claim by the contract that minted the token. A pool is not. */
const LAUNCH_KINDS = new Set([
  'PONS_TOKEN_LAUNCHED',
  'POOLS_TRADE_INSTANT_LAUNCHED',
  'POOLS_TRADE_CROWD_TRADABLE',
  'POOLS_TRADE_TOKEN_CREATED',
  'POOLS_TRADE_CROWD_AUCTION_CREATED',
  'LAUNCHPAD_TOKEN_CREATED',
  'POOLS_FUN_TOKEN_LAUNCHED',
]);

export function originTone(origin: Origin | null): 'launch' | 'pool' | 'unknown' {
  if (origin === null || origin.event_kind === null) return 'unknown';
  return LAUNCH_KINDS.has(origin.event_kind) ? 'launch' : 'pool';
}

export function originLabel(origin: Origin | null): string {
  if (origin === null || origin.event_kind === null) return 'Unclassified';
  return LABELS[origin.event_kind] ?? origin.event_kind;
}

export function OriginBadge({ origin }: { origin: Origin | null }) {
  const tone = originTone(origin);
  const label = originLabel(origin);

  // INVARIANT 1. Rendered rather than skipped: a pair from before the
  // launchpad-first scanner is unknown provenance, and unknown is a fact.
  if (origin === null) {
    return (
      <span
        className="chip"
        data-origin="unknown"
        title="Discovered before the launchpad-first scanner, so its provenance was never recorded. Unknown, not ordinary."
      >
        Unclassified
      </span>
    );
  }

  const blocked = origin.trade_block_reasons.length > 0;

  // New-vs-existing is the question the feed is scanned for, so it is stated in
  // words rather than left encoded in an event-kind name. `KNOWN_TOKEN_NEW_LP`
  // is a sentence about a token, not a label anyone reads at a glance.
  const novelty =
    origin.from_launchpad || origin.event_kind === 'DEX_NATIVE_TOKEN_CANDIDATE'
      ? 'New token'
      : origin.event_kind === 'KNOWN_TOKEN_NEW_LP'
        ? 'Existing token'
        : null;

  return (
    <span className="origin-badge">
      <span
        className="chip"
        data-origin={tone}
        title={origin.classification_reason ?? undefined}
      >
        {label}
      </span>

      {novelty && (
        <span
          className="chip"
          data-novelty={novelty === 'New token' ? 'new' : 'existing'}
          title={
            novelty === 'New token'
              ? 'First time this token has been seen.'
              : 'This token was already known; this is another pool for it.'
          }
        >
          {novelty}
        </span>
      )}

      {origin.from_launchpad && (
        <span
          className="chip"
          data-tone="muted"
          title="Attributed from the launchpad's own event, not inferred from the pool."
        >
          Verified launch
        </span>
      )}

      {origin.canonical_for_token && (
        <span className="chip" data-tone="muted" title="The pool the launchpad itself created for this token, as opposed to any later pool anyone can open against it.">
          Canonical
        </span>
      )}

      {/* INVARIANT 3. Stated, never merely implied by absence. */}
      {blocked && (
        <span
          className="chip"
          data-origin="blocked"
          title={`Trading blocked: ${origin.trade_block_reasons.join(', ')}`}
        >
          Trading blocked
        </span>
      )}
    </span>
  );
}
